/**
 * Ícones em SVG inline. Sem biblioteca de propósito: são doze glifos, e uma
 * dependência de ícones custa mais bytes do que o ficheiro inteiro.
 * `currentColor` em tudo — a cor é decidida pelo CSS, nunca aqui.
 */

import type { ReactNode } from "react";

interface IconProps {
  size?: number;
  className?: string;
}

function svg(path: ReactNode, viewBox = "0 0 24 24") {
  return function Icon({ size = 20, className }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox={viewBox}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className={className}
      >
        {path}
      </svg>
    );
  };
}

export const IconHash = svg(<path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" />);

export const IconSpeaker = svg(
  <>
    <path d="M11 5 6 9H3v6h3l5 4V5Z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" />
  </>,
);

export const IconSpeakerOff = svg(
  <>
    <path d="M11 5 6 9H3v6h3l5 4V5Z" />
    <path d="m16 9 5 6M21 9l-5 6" />
  </>,
);

export const IconMic = svg(
  <>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
  </>,
);

// O mesmo microfone de cima, INTEIRO, com um corte por cima. A versão anterior
// tentava desenhar a cápsula partida ao meio pelo corte (dois arcos truncados e
// meia haste): em grande via-se a intenção, aos 20px do botão via-se um rabisco.
// Um símbolo que já se lê não precisa de ser redesenhado para ser negado — é o
// mesmo desenho, mais uma linha.
export const IconMicOff = svg(
  <>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    <path d="M3.5 3.5l17 17" />
  </>,
);

export const IconHeadphones = svg(
  <>
    <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
    <rect x="2" y="14" width="5" height="7" rx="2" />
    <rect x="17" y="14" width="5" height="7" rx="2" />
  </>,
);

// A mesma lição do `IconMicOff`, e este estava pior: o arco partia-se a meio
// para "abrir caminho" ao corte e sobrava um gancho solto entre duas caixas —
// aos 20px lia-se como uma tesoura. Auscultadores INTEIROS, um corte por cima.
export const IconHeadphonesOff = svg(
  <>
    <path d="M3 13.5v-1.5a7.5 7.5 0 0 1 15 0v1.5" />
    <rect x="1" y="13.5" width="4.5" height="6.5" rx="2" />
    <rect x="15.5" y="13.5" width="4.5" height="6.5" rx="2" />
    <path d="m18.6 4.4 4.4 4.4M23 4.4l-4.4 4.4" />
  </>,
);

// PARTILHAR o ecrã — um monitor com uma seta a SAIR dele, para cima. Tinha um
// `>` no meio, que se lê "seguinte" ou "reproduzir" e não diz nada sobre enviar
// o que quer que seja; e, pior, deixava-o irmão do quadro branco (outro
// rectângulo com um sinal lá dentro) na MESMA fila de botões. A seta é o verbo:
// isto sai daqui para os outros.
export const IconScreen = svg(
  <>
    <rect x="2" y="4" width="20" height="13" rx="2" />
    <path d="M8 21h8M12 17v4" />
    <path d="M12 13.5V7.5M9.2 10.3 12 7.5l2.8 2.8" />
  </>,
);

export const IconCamera = svg(
  <>
    <rect x="2" y="6" width="13" height="12" rx="2" />
    <path d="m15 11 6-3.5v9L15 13Z" />
  </>,
);

// DESLIGAR — o auscultador POUSADO, virado ao contrário. Era um telefone, mais
// um corte na diagonal, mais uma seta no canto: três símbolos empilhados em
// 24px que davam um novelo, e aos 20px do botão nem novelo, uma mancha. Aqui é
// UM símbolo só, o mesmo de qualquer telefone, rodado 135° — a mesma metáfora
// de pousar o auscultador no descanso que toda a gente já leu mil vezes. O
// vermelho do botão faz o resto; não é preciso negá-lo com um traço por cima.
export const IconPhoneOff = svg(
  <g transform="rotate(133 12 12)">
    <path d="M7.4 3.6c1.2-1.1 2.9-.5 3.5.6l.9 1.7c.4.7.2 1.5-.3 2l-1 .9a10.5 10.5 0 0 0 4.7 4.7l.9-1c.5-.5 1.3-.7 2-.3l1.7.9c1.1.6 1.7 2.3.6 3.5l-1.2 1.2c-.9.9-2.2 1.2-3.3.8A16.5 16.5 0 0 1 5.1 8.1c-.4-1.1-.1-2.4.8-3.3Z" />
  </g>,
);

export const IconSettings = svg(
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2v.2a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.6 1.7 1.7 0 0 0-1.9.4l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0-1.2-2.9H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1.1 1.7 1.7 0 0 0-.4-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H10a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V10a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
  </>,
);

export const IconPlus = svg(<path d="M12 5v14M5 12h14" />);

export const IconSend = svg(<path d="M4 12 20 4l-8 16-2-6-6-2Z" />);

export const IconMembers = svg(
  <>
    <path d="M16 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 20v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" />
  </>,
);

export const IconUserPlus = svg(
  <>
    <path d="M15 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <path d="M19 8v6M22 11h-6" />
  </>,
);

export const IconReply = svg(
  <>
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h10a6 6 0 0 1 6 6v5" />
  </>,
);

/**
 * Tópico: uma conversa DENTRO de uma conversa — daí a bolha com as linhas lá
 * dentro. Deliberadamente diferente do `IconReply` ao lado, que é uma seta:
 * responder devolve uma linha ao canal, abrir um tópico leva a conversa embora.
 */
export const IconThread = svg(
  <>
    <path d="M4 5h16v10H9l-5 4V5Z" />
    <path d="M8 9h8M8 12h5" />
  </>,
);

export const IconPin = svg(
  <>
    <path d="M12 17v5" />
    <path d="M9 3h6l-1 6 3 3v2H7v-2l3-3-1-6Z" />
  </>,
);

export const IconSearch = svg(
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </>,
);

/** Seta para baixo: voltar ao fim do histórico depois de saltar para trás. */
export const IconArrowDown = svg(<path d="M12 5v14M6 13l6 6 6-6" />);

export const IconTrash = svg(
  <>
    <path d="M4 7h16M10 11v6M14 11v6" />
    <path d="M6 7l1 13h10l1-13M9 7V4h6v3" />
  </>,
);

export const IconEdit = svg(
  <>
    <path d="M4 20h4l10-10-4-4L4 16v4Z" />
    <path d="m14 6 4 4" />
  </>,
);

export const IconClose = svg(<path d="M6 6l12 12M18 6 6 18" />);

/** Caixa com visto: entrar no modo de apagar várias mensagens de uma vez. */
export const IconCheckSquare = svg(
  <>
    <path d="M20 11v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" />
    <path d="m9 11 3 3 8-8" />
  </>,
);

export const IconChevron = svg(<path d="m6 9 6 6 6-6" />);

export const IconMore = svg(
  <>
    <circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="19" cy="12" r="1.5" fill="currentColor" stroke="none" />
  </>,
);

export const IconLock = svg(
  <>
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </>,
);

export const IconGrid = svg(
  <>
    <rect x="3" y="3" width="7" height="7" rx="1.4" />
    <rect x="14" y="3" width="7" height="7" rx="1.4" />
    <rect x="3" y="14" width="7" height="7" rx="1.4" />
    <rect x="14" y="14" width="7" height="7" rx="1.4" />
  </>,
);

export const IconMove = svg(
  <>
    <path d="M12 3v18M3 12h18" />
    <path d="m8 6 4-3 4 3M8 18l4 3 4-3M6 8l-3 4 3 4M18 8l3 4-3 4" />
  </>,
);

export const IconShield = svg(
  <>
    <path d="M12 3l7 3v5c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6l7-3z" />
    <path d="M9.5 12l1.8 1.8L15 10" />
  </>,
);

// QUADRO BRANCO — um cavalete: tela, pernas abertas, e um traço desenhado lá
// dentro. Era um rectângulo com pé de monitor e um visto (✓) no meio: lia-se
// "ecrã confirmado", e ficava ao lado do botão de partilhar ecrã — outro
// rectângulo — na mesma fila. Duas coisas diferentes com a mesma silhueta são
// duas coisas que ninguém distingue com o rato a passar. O que separa isto de
// um monitor são as PERNAS (um monitor tem pé, um cavalete tem pernas abertas)
// e o rabisco, que é o que lá se vai fazer.
export const IconBoard = svg(
  <>
    <rect x="3" y="3" width="18" height="12" rx="2" />
    <path d="M12 15v3M8.2 21 12 18l3.8 3" />
    <path d="M6.9 8.8c1.2-2.2 2.5-2.2 3.7 0s2.5 2.2 3.7 0" />
    <path d="M7.4 12.2h6.4" />
  </>,
);

export const IconBrush = svg(
  <>
    <path d="M14 4l6 6-7 7-3 1-3-1 1-3 9-9z" />
    <path d="M6 15c-2 1-2 4-4 5 2 .5 5 0 6-2" />
  </>,
);

export const IconEraser = svg(
  <>
    <path d="M4 15l7-7 6 6-4 4H8l-4-4z" />
    <path d="M20 20H9M11 11l6 6" />
  </>,
);

export const IconText = svg(<path d="M6 5h12M12 5v14M9 19h6" />);

export const IconUndo = svg(
  <>
    <path d="M9 7 5 11l4 4" />
    <path d="M5 11h9a5 5 0 0 1 0 10H9" />
  </>,
);

export const IconHand = svg(
  <>
    <path d="M8 12V5a1.4 1.4 0 0 1 2.8 0v6" />
    <path d="M10.8 11V4a1.4 1.4 0 0 1 2.8 0v7" />
    <path d="M13.6 11.5V6.5a1.4 1.4 0 0 1 2.8 0V14a6 6 0 0 1-6 6 6 6 0 0 1-5.2-3l-2-3.4a1.4 1.4 0 0 1 2.4-1.4L8 14" />
  </>,
);

// REAGIR / pôr um emoji — a carinha com um `+` no canto. Sozinha, uma carinha
// é um estado ("estou contente"), e é assim que se lê num botão: como se fosse
// mudar o meu humor. Com o `+` passa a ser um VERBO — acrescentar um. O círculo
// abre-se para lhe dar lugar em vez de o sobrepor: dois traços cruzados por
// cima da borda, aos 20px, são um borrão.
export const IconSmile = svg(
  <>
    <path d="M20.6 13.5A9 9 0 1 1 14.5 3.4" />
    <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
    <path d="M9 9.5h.01M15 9.5h.01" />
    <path d="M19 2.5v5M16.5 5h5" />
  </>,
);

// Campainha A TOCAR — as duas ondas laterais são o que a separa do sino parado
// de "notificações". Aqui o gesto é chamar alguém, não listar avisos.
export const IconBell = svg(
  <>
    <path d="M17.5 14.5v-3.5a5.5 5.5 0 1 0-11 0v3.5l-1.4 2.1a.6.6 0 0 0 .5.9h12.8a.6.6 0 0 0 .5-.9l-1.4-2.1Z" />
    <path d="M10 20.5a2.2 2.2 0 0 0 4 0" />
    <path d="M2.8 8.2A6.4 6.4 0 0 1 5 4M21.2 8.2A6.4 6.4 0 0 0 19 4" />
  </>,
);

// Sino PARADO e cortado — silenciado. Sem as ondas laterais do de cima de
// propósito: aquele é o gesto de chamar alguém, este é o de calar um sítio.
export const IconBellOff = svg(
  <>
    <path d="M17.5 14.5v-3.5a5.5 5.5 0 0 0-7.6-5.1M6.5 9.4v5.1l-1.4 2.1a.6.6 0 0 0 .5.9h11" />
    <path d="M10 20.5a2.2 2.2 0 0 0 4 0" />
    <path d="M3.5 3.5l17 17" />
  </>,
);

export const IconExpand = svg(
  <>
    <path d="M8 3H4a1 1 0 0 0-1 1v4M16 3h4a1 1 0 0 1 1 1v4" />
    <path d="M8 21H4a1 1 0 0 1-1-1v-4M16 21h4a1 1 0 0 0 1-1v-4" />
  </>,
);

export const IconShrink = svg(
  <>
    <path d="M4 8h3a1 1 0 0 0 1-1V4M20 8h-3a1 1 0 0 1-1-1V4" />
    <path d="M4 16h3a1 1 0 0 1 1 1v3M20 16h-3a1 1 0 0 0-1 1v3" />
  </>,
);

/** Só existe no telemóvel: abre a gaveta com a rail de servidores e os canais. */
/** Lua: "ausente". Um quarto crescente lê-se a 13px, uma palavra não. */
export const IconMoon = svg(
  <path d="M20 14.2A8.2 8.2 0 0 1 9.8 4a8.5 8.5 0 1 0 10.2 10.2Z" fill="currentColor" stroke="none" />,
);

export const IconMenu = svg(<path d="M4 7h16M4 12h16M4 17h16" />);

/** Balão de conversa — o chat do escritório. */
export const IconChat = svg(<path d="M4 5h16v11H9l-5 4V5Z" />);

/** Mapa dobrado — o mapa grande do escritório. */
export const IconMap = svg(
  <>
    <path d="m3 6 6-2 6 2 6-2v14l-6 2-6-2-6 2V6Z" />
    <path d="M9 4v14M15 6v14" />
  </>,
);
